import type { ClientEnrollmentData } from '@/features/enrollment'
import { validateExploitationStartDate, validateDOBByRelationship, calculateAgeFromDOB, validatePhone } from '@/shared/utils'

// Helper function to calculate age from DOB (same as Step 3)
const calculateAge = (dob: string): number => {
  const birthDate = new Date(dob)
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age
}

// Validation function for Roblox Intake Contact
export const validateRobloxIntakeContact = (data: ClientEnrollmentData, legalGuardianConfirmed: boolean = false) => {
  const errors: { [key: string]: string } = {}
  
  // PREQUALIFICATION VALIDATION
  
  // Q1: State Selection - Florida = DQ
  if (!data.intakeState) {
    errors.intakeState = 'State selection is required'
  } else if (data.intakeState.toLowerCase() === 'florida') {
    errors.intakeState = 'DQ: Florida residents are not eligible for this campaign'
  }
  
  // Q2: Date of birth of Gamer
  if (!data.intakeDOB) {
    errors.intakeDOB = 'Date of birth of gamer is required'
  } else {
    // Validate DOB based on relationship to victim (same as Step 3)
    const dobValidation = validateDOBByRelationship(data.intakeDOB, data.relationToVictim || '')
    if (!dobValidation.isValid && dobValidation.errorMessage) {
      errors.intakeDOB = dobValidation.errorMessage
    }
    
    // ✅ VALIDATION: Check if minor status matches the date of birth
    // If user says "Are you a minor (under age of 18)?" is "No", 
    // then the date of birth must show they are 18 or older
    if (data.isMinor === false && data.intakeDOB) {
      const clientAge = calculateAge(data.intakeDOB)
      if (clientAge < 18) {
        errors.intakeDOB = `Minor Age Restriction: The gamer is ${clientAge} years old. A parent or legal guardian must be present and must sign up the gamer because the gamer is a minor under the age of 18.`
        errors.isMinor = 'The date of birth indicates the gamer is a minor. Please select "Yes" for minor status.'
      }
    }
    
    // Campaign age eligibility validation (same as Step 3)
    const campaign = data.campaign
    if (campaign && (campaign.minAge !== null || campaign.maxAge !== null)) {
      const clientAge = calculateAge(data.intakeDOB)
      const hasMinAge = campaign.minAge !== null && campaign.minAge !== undefined
      const hasMaxAge = campaign.maxAge !== null && campaign.maxAge !== undefined
      
      // Check if client age is outside the allowed range
      if (hasMinAge && clientAge < campaign.minAge) {
        if (hasMaxAge) {
          errors.intakeDOB = `Client must be between ${campaign.minAge} and ${campaign.maxAge} years old for this campaign (current age: ${clientAge})`
        } else {
          errors.intakeDOB = `Client must be at least ${campaign.minAge} years old for this campaign (current age: ${clientAge})`
        }
      } else if (hasMaxAge && clientAge > campaign.maxAge) {
        if (hasMinAge) {
          errors.intakeDOB = `Client must be between ${campaign.minAge} and ${campaign.maxAge} years old for this campaign (current age: ${clientAge})`
        } else {
          errors.intakeDOB = `Client must be ${campaign.maxAge} years old or younger for this campaign (current age: ${clientAge})`
        }
      }
    }
  }
  
  // Q3: Did incident happen less than 2 years ago - DQ if more than 2 years ago
  if (data.incidentHappenedLessThan2Years === undefined || data.incidentHappenedLessThan2Years === null) {
    errors.incidentHappenedLessThan2Years = 'Please specify if the incident happened less than 2 years ago'
  } else if (data.incidentHappenedLessThan2Years === false) {
    // If "No" is selected, it means the incident happened more than 2 years ago - DQ
    errors.incidentHappenedLessThan2Years = 'DQ: Incident happened more than 2 years ago - not eligible'
  }
  
  // Q4: Do you know Roblox gamer tag
  if (data.knowsRobloxGamerTag === undefined || data.knowsRobloxGamerTag === null) {
    errors.knowsRobloxGamerTag = 'Please specify if you know the Roblox gamer tag'
  }
  
  // Q4-2: Roblox gamer tag verification is optional but recommended if "Yes" is selected
  // We don't require it to pass validation, but it's good to have if available
  
  // Basic contact information validation
  if (!data.relationToVictim) errors.relationToVictim = 'This field is required. Please select a value.'
  
  if (!data.intakeFirstName) errors.intakeFirstName = 'First Name is required'
  if (!data.intakeLastName) errors.intakeLastName = 'Last Name is required'
  
  // Phone validation with American format
  if (!data.intakePhone) {
    errors.intakePhone = 'Phone is required'
  } else {
    const phoneValidation = validatePhone(data.intakePhone)
    if (!phoneValidation.isValid) {
      errors.intakePhone = phoneValidation.error || 'Phone number must be a valid American phone number'
    }
  }
  
  if (!data.intakeEmail) errors.intakeEmail = 'Email is required'
  
  // Auto-set consent to true for Roblox (not shown in UI)
  if (!data.intakeConsent) {
    // This will be set automatically when saving
  }

  // CRITICAL: Prevent minors from enrolling themselves
  if (data.relationToVictim === 'Myself' && data.isMinor === true) {
    errors.isMinor = 'Minors can not enroll themselves'
    errors.relationToVictim = 'Minors can not enroll themselves - please select "Loved one" if calling on behalf of a minor'
  }

  // Minor status validation
  if (data.isMinor === undefined || data.isMinor === null) {
    errors.isMinor = 'Minor status is required'
  }

  // If minor, validate verbal permission and relationship
  if (data.isMinor === true) {
    if (!data.verbalPermission) {
      errors.verbalPermission = 'Verbal permission status is required for minors'
    } else if (data.verbalPermission === 'No verbal permission') {
      errors.verbalPermission = 'DQ: Verbal permission must be given to proceed'
    }

    if (!data.relationshipToMinor) {
      errors.relationshipToMinor = 'Relationship to minor is required'
    } else if (data.relationshipToMinor === 'None') {
      errors.relationshipToMinor = 'DQ: Must have legal relationship to minor'
    }
  }

  // Note: Video Gamer fields (callerFirstName/callerLastName) validation is moved to Step 1 (Injured Party)
  // They are optional in Step 0 (Intake Contact) and will be validated in Step 1

  // Legal guardian confirmation for adult loved ones
  if (data.relationToVictim === 'Loved one' && data.isMinor === false && !legalGuardianConfirmed) {
    errors.legalGuardianRequired = 'You must confirm that you are the legal guardian of this adult to proceed.'
  }
  
  return errors
}
